import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { PartyPopper, XCircle, AlertTriangle, CheckCircle2, Ban, Mail, Bell } from 'lucide-react';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();
  
  useEffect(() => {
    api.get('/notifications').then(res => {
      setNotifications(res.data);
    });
  }, []);
  
  const markAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, lu: true } : n
      ));
    } catch (err) {
      console.error(err);
    }
  };

  const handleGoToLink = async (notification) => {
    // Marquer comme lu si pas encore lu
    if (!notification.lu) {
      await markAsRead(notification.id);
    }
    // Naviguer vers le lien
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const getNotificationStyle = (type, lu) => {
    if (lu) return 'bg-gray-50 text-gray-600 border-gray-200';
    
    switch (type) {
      case 'author_request_approved':
        return 'bg-green-50 text-green-800 border-green-400';
      case 'author_request_refused':
        return 'bg-red-50 text-red-800 border-red-400';
      case 'report_action':
        return 'bg-orange-50 text-orange-800 border-orange-400';
      default:
        return 'bg-indigo-50 text-indigo-800 border-indigo-400';
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'author_request_approved':
        return <PartyPopper size={24} className="text-green-500" />;
      case 'author_request_refused':
        return <XCircle size={24} className="text-red-500" />;
      case 'report_action':
        return <AlertTriangle size={24} className="text-amber-500" />;
      case 'application_accepted':
        return <CheckCircle2 size={24} className="text-green-500" />;
      case 'application_rejected':
        return <Ban size={24} className="text-red-500" />;
      case 'new_application':
        return <Mail size={24} className="text-blue-500" />;
      default:
        return <Bell size={24} className="text-gray-500" />;
    }
  };

  if (!notifications.length) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Bell size={40} className="mx-auto mb-2 text-gray-400" />
        Aucune notification
      </div>
    );
  }

  return (
    <div className="w-full my-4 space-y-3">
      {notifications.map(n => (
        <div
          key={n.id}
          className={`p-4 rounded-lg shadow-sm border-l-4 transition-all ${getNotificationStyle(n.type, n.lu)} ${!n.lu ? 'ring-1 ring-opacity-20' : ''}`}
        >
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0">{getNotificationIcon(n.type)}</span>
            <div className="flex-1 min-w-0">
              {/* Message avec sauts de ligne préservés */}
              <div className="text-sm whitespace-pre-line leading-relaxed">
                {n.message}
              </div>
              
              {/* Date et actions */}
              <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-200">
                <span className="text-xs text-gray-500">
                  {new Date(n.created_at).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
                <div className="flex items-center gap-2">
                  {n.link && (
                    <button
                      onClick={() => handleGoToLink(n)}
                      className="text-xs px-3 py-1 bg-indigo-600 text-white rounded-full shadow-sm hover:bg-indigo-700 transition"
                    >
                      Voir →
                    </button>
                  )}
                  {!n.lu && (
                    <button
                      onClick={() => markAsRead(n.id)}
                      className="text-xs px-3 py-1 bg-white rounded-full shadow-sm hover:bg-gray-50 transition"
                    >
                      Marquer comme lu
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default Notifications;
